// ─────────────────────────────────────────────────────────────────────────────
// rosterState.ts — WHAT COLOUR AN AGENT IS ON THE OVERVIEW ROSTER. Pure.
//
// OWNER RULING 2026-09-01:
//
//     live and autonomous ......... green
//     live, not autonomous ........ orange
//     off ......................... red
//
// and a fourth the ruling implies rather than lists:
//
//     ⭐ THE STATE CANNOT BE READ ... grey
//
// ── DO NOT INFER GREEN ─────────────────────────────────────────────────────
// "A roster that shows green because nothing said otherwise is the same fault
// as an activity chip that reads `read` because no payload disagreed." So
// `autonomous` is THREE-VALUED on the wire — true / false / null — and null is
// never collapsed. It is not orange either: orange is the claim "this agent is
// live and is NOT autonomous", which is as much a claim as green.
//
// Today that matters immediately: on GROWTH only Buck has a stored toggle
// value, so Delta, Doe, Echo and Ilsa are UNRULED and render grey. Orange is
// reachable and real — TRADING:arthur is stored `false` — but no GROWTH agent
// is in it, and a roster that painted the four of them orange would be
// asserting a ruling nobody made.
//
// ── THE DOT COSTS NOTHING, AND THAT IS STRUCTURAL ──────────────────────────
// The per-agent loop route costs 2653 ms of hub-blocking for Buck (measured on
// an idle box). Six to ten roster rows would be that many, on the box this
// surface exists to keep responsive — the fan-out `RunDot`'s `mayRead=false`
// exists to prevent. So the autonomy fact is joined ONCE, producer-side, in
// `core/os_structure.build_os_structure` from a single `loop_toggle.table()`
// read, and rides the projection the roster already reads. THE ROSTER MAKES NO
// REQUEST OF ITS OWN, per row or otherwise.
//
// ── AND ALPHA IS A DIFFERENT QUESTION ──────────────────────────────────────
// Every other agent's colour answers "is this agent autonomous". Alpha's
// answers "is the harness healthy", because Alpha IS the harness — green while
// it runs, orange when the chain recorded a fault, red when it is down.
//
// DOWN IS THE ONE STATE A PRODUCER CANNOT REPORT. A stopped Alpha publishes
// nothing, so red is derived HERE from the publication going stale — never from
// anything `alpha_board` wrote. That is why self-reporting is sound for this
// one dot and not for the activity feed.
// ─────────────────────────────────────────────────────────────────────────────

/** The four renderings. `grey` is "no answer", never a state of the agent. */
export type RosterDotState = 'green' | 'orange' | 'red' | 'grey';

/** The projection's per-agent shape this module needs. */
export interface RosterAgent {
  name?: string;
  implementationState?: string;
  /** true / false / null — null is "nobody has ruled", never off. */
  autonomous?: boolean | null;
  role?: { id: string; label: string } | null;
}

/** `alpha_board`'s `ceo_health`, as published into the `ceo` root. */
export interface CeoHealth {
  state?: 'ok' | 'degraded' | null;
  at?: string;
  next_expected_at?: string;
  period_seconds?: number;
  why?: string;
  slot?: Record<string, unknown> | null;
}

/**
 * IS THIS AGENT LIVE?
 *
 * ⭐ THE BUG THIS REPLACES SHIPPED AND WAS ON SCREEN. `panels.tsx:282` tested
 * `a.implementationState === 'live'` while the registry projection writes
 * `LIVE_BUILT` — measured, all six GROWTH agents — so the comparison COULD
 * NEVER BE TRUE and every dot on the Overview roster was the idle one. The
 * identical comparison was fixed on the rail on 2026-08-31 and this twin was
 * left behind: an instance fix, where the class needed sweeping.
 *
 * It is a PREFIX test on the normalised value, so `LIVE_BUILT`, `live` and any
 * future `LIVE_*` all read live and no single spelling is load-bearing.
 */
export function isLive(implementationState?: string): boolean {
  return String(implementationState || '').trim().toUpperCase().startsWith('LIVE');
}

/** Does this agent's colour come from the harness health rather than a toggle? */
export function isHarnessCeo(agent: RosterAgent): boolean {
  // BY DECLARED ROLE, never by name. `harness_topology` declares ALPHA →
  // {id: 'domain_ceo'} and roles are derived everywhere else; a name test here
  // would be the roster's own copy of a mapping the topology already owns.
  return (agent.role?.id || '') === 'domain_ceo';
}

/**
 * ALPHA'S DOT. Green while it runs, orange on a recorded fault, red when down.
 *
 * `grey` when there is nothing to read — no health key at all. That is the
 * never-infer rule applied to the harness: a missing health block is not "it is
 * fine", and it is not "it is down" either.
 */
export function ceoDotState(health: CeoHealth | null | undefined,
                            now: number = Date.now()): RosterDotState {
  if (!health || typeof health !== 'object') return 'grey';
  // ── RED IS DERIVED HERE, FROM SILENCE ────────────────────────────────────
  // One WHOLE missed period past the instant Alpha itself said to expect the
  // next one. The period is the producer's own figure, so this holds no second
  // copy of Alpha's cadence.
  const next = Date.parse(String(health.next_expected_at || ''));
  const period = typeof health.period_seconds === 'number'
    && Number.isFinite(health.period_seconds) && health.period_seconds > 0
    ? health.period_seconds * 1000 : null;
  if (Number.isFinite(next) && period !== null && now > next + period) return 'red';
  if (health.state === 'degraded') return 'orange';
  if (health.state === 'ok') return 'green';
  // A published block with no verdict — a ledger that has never ticked. Not a
  // fault, not health either.
  return 'grey';
}

/**
 * THE ROSTER DOT, for any agent. ONE resolver, so the panel holds no colour
 * rule of its own and a fifth state cannot appear in a JSX branch.
 */
export function rosterDotState(
  agent: RosterAgent, health?: CeoHealth | null, now: number = Date.now(),
): RosterDotState {
  if (isHarnessCeo(agent)) return ceoDotState(health, now);
  // OFF. Not built, or built and not live — either way there is nothing running
  // to be autonomous.
  if (!isLive(agent.implementationState)) return 'red';
  if (agent.autonomous === true) return 'green';
  if (agent.autonomous === false) return 'orange';
  // null / undefined / anything else. NOBODY HAS RULED, or the toggle could not
  // be read — and neither is a colour about the agent.
  return 'grey';
}

/** The one-word caption beside the dot. It says what was READ, never a guess. */
export function rosterDotLabel(
  agent: RosterAgent, health?: CeoHealth | null, now: number = Date.now(),
): string {
  const dot = rosterDotState(agent, health, now);
  if (isHarnessCeo(agent)) {
    return dot === 'green' ? 'running'
      : dot === 'orange' ? 'degraded'
      : dot === 'red' ? 'not reporting'
      : 'unread';
  }
  return dot === 'green' ? 'autonomous'
    : dot === 'orange' ? 'not autonomous'
    : dot === 'red' ? 'off'
    : 'unruled';
}

// ─────────────────────────────────────────────────────────────────────────────
// THE STATUS BANNER (owner ruling 2026-09-02)
//
// GROWTH ▸ CEO carried a fixed line of copy above REPORTING TOTALS that said the
// same thing whether the agent was healthy or dead — and Alpha had been red on
// the rail since 10:10 with the page never saying why.
//
// ⭐⭐⭐ ONE SOURCE. The banner is the THIRD surface to show Alpha, after the rail
// dot and the Overview roster dot. It does not read `ceo_health` itself and holds
// no verdict rule: it calls `ceoDotState` — the same function, on the same block —
// and the state it paints is BY CONSTRUCTION the state they paint. Three things
// saying different truths about one agent is worse than one saying nothing.
//
// ⭐ AND THE RED SENTENCE IS NOT `why`. This is the whole honesty of it: red is
// derived HERE, from the publication going stale, and the stale block's own `why`
// is whatever was true when it was last written — right now, on the live board,
// that string reads "the last pulse completed and its notes landed". Printing the
// producer's words under a red dot would put the most reassuring sentence on the
// page at the moment the agent stopped talking. So red states the ARITHMETIC the
// redness came from — when the last publication was, and when the next was due —
// and both numbers are the block's own.
//
// A CLAIM IS A CLAIM IN EITHER DIRECTION. Where the health cannot be read there is
// NO status line: not green, not orange, not a word. The standing description
// still renders, because what the CEO *is* remains true whether or not it is
// running — but nothing on the strip asserts anything about its health.
// ─────────────────────────────────────────────────────────────────────────────

/** What the banner shows. `claim === null` is NO CLAIM, and it is not a state. */
export interface CeoBannerLine {
  /** Same value the rail dot and the roster dot carry, from the same resolver. */
  state: RosterDotState;
  /** The status line — FEW WORDS. Null where nothing may be claimed. */
  claim: string | null;
  /** The long form, for the hover title. Never on the strip: the panels below
   *  carry the detail, and a control answers rather than explaining itself. */
  detail: string | null;
}

/**
 * HH:MM OUT OF AN ISO STRING, IN THE STRING'S OWN OFFSET.
 *
 * ⭐ NOT `Date`, and that is deliberate. A bar pinning a formatted clock failed
 * on a working build once already, because `toLocaleTimeString` renders in the
 * RUNNER's timezone — so the same block read 10:10 on this box and 00:10 in CI.
 * Alpha writes its stamps with its own offset attached; reading the wall-clock
 * digits out of the string returns the hour the producer meant, on any machine.
 */
export function clockLabel(iso?: string | null): string | null {
  const m = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(String(iso || ''));
  return m ? `${m[1]}:${m[2]}` : null;
}

/** The producer's reasons, split as it joined them. */
function reasons(why?: string | null): string[] {
  return String(why || '').split(';').map((s) => s.trim()).filter(Boolean);
}

/**
 * THE BANNER LINE for the harness CEO's root.
 *
 * The state is `ceoDotState`'s, always. Only the WORDS are decided here.
 */
export function ceoBannerState(health: CeoHealth | null | undefined,
                               now: number = Date.now()): CeoBannerLine {
  const state = ceoDotState(health, now);
  const why = (health && typeof health === 'object' ? health.why : null) || null;
  const all = reasons(why);

  if (state === 'grey') return { state, claim: null, detail: null };

  if (state === 'green') {
    // Nothing is wrong, so there is nothing to report and the owner's words say
    // exactly that. The standing description carries the rest of the strip.
    return { state, claim: 'All systems go', detail: why };
  }

  if (state === 'orange') {
    // THE PRODUCER'S OWN REASON, first one only. `_health` joins up to four
    // clauses with "; " and all four on the strip is a report, not a status
    // line — the count says the others exist and the hover holds them.
    const head = all[0] || 'degraded — no reason stated';
    const rest = all.length - 1;
    return { state, claim: rest > 0 ? `${head} (+${rest} more)` : head, detail: why };
  }

  // RED — derived from silence, so it says what the silence is.
  const at = clockLabel(health?.at);
  const due = clockLabel(health?.next_expected_at);
  const claim = at
    ? `nothing published since ${at} · one was due ${due}`
    : `nothing published · one was due ${due}`;
  return {
    state,
    claim,
    // The stale verdict is NOT repeated as the reason. What the hover holds is
    // the arithmetic and, named as stale, what the block last said.
    detail: `last published ${health?.at || 'unknown'}; next was due `
          + `${health?.next_expected_at || 'unknown'}; the last reading said `
          + `"${why || 'nothing'}" and has not been refreshed since`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// THE RAIL'S ROOT DOT (owner ruling 2026-09-02 — option B)
//
// It was `Object.prototype.hasOwnProperty.call(feed.board, r.key)`: the dot
// answered "does a board key with this root's name exist". The live GROWTH board
// holds exactly two roots, and `overview` is not one of them and never will be —
// the Overview panels read `ceo`, `pricing_budgets`, `supporting_systems` and
// `integrations`, never a root of their own. So the Overview dot had been idle
// since it shipped, correctly answering a question nobody was asking.
//
// ⭐ THE RULING: THE DOT MEANS THE PANELS THIS ROOT READS HAVE DATA. Not that a
// key exists — that is decoration, green because something was spelled right.
// And NOT by writing an `overview` root to make the key appear: that is the
// board-written-by-the-thing-it-verifies shape already forbidden for the
// activity feed, and it would be the same fault in a new place.
//
// ── WHERE A ROOT READS FROM IS A DECLARATION, NOT A LIST KEPT HERE ──────────
// A root whose panels are CUSTOM components reads wherever those components
// read, and `PANEL_ROOT` is that declaration — the very map `KpiRow`,
// `BudgetRollup`, `CeoReportPreview`, `TelcoRollup` and `ApiChangeWatch` look
// their data up through. A root with DECLARED panels reads its own board root,
// because `DeclaredPanel` is handed `board[root.key][panel.key]`. One authority:
// if a panel is repointed in `PANEL_ROOT`, this dot follows it in the same edit.
//
// ── DO NOT INFER GREEN ─────────────────────────────────────────────────────
// Three values, and the third is the point. `unread` is the surface read not
// having landed — it renders as ABSENCE (a hollow ring), never as health and
// never as the dim "nothing here yet", because "we could not look" and "we
// looked and it is empty" are different answers and only one of them is about
// the root.
//
// ── WHAT THIS DOT DOES NOT ANSWER ──────────────────────────────────────────
// The Agent roster panel reads the REGISTRY, not the board, so it is not one of
// the sources counted here. That is deliberate rather than an omission: the
// registry read is what `ready` already reports, so the two questions stay
// separate — `ready` says the surface read landed, the sources say the panels
// have something to show.
// ─────────────────────────────────────────────────────────────────────────────

/** Three answers. `unread` is the absence of one, never a state of the root. */
export type RootDataState = 'live' | 'empty' | 'unread';

/** The shape of a surface root this module needs. */
export interface RootLike {
  key: string;
  panels: Array<{ custom?: boolean }>;
}

/** Is there anything in this board root? An EMPTY object is not data — it is
 *  the key existing, which is exactly the answer being replaced. */
export function hasBoardData(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value !== 'object') return false;
  return Object.keys(value as Record<string, unknown>).length > 0;
}

/**
 * WHICH BOARD ROOTS THIS SURFACE ROOT READS.
 *
 * Derived from the root's own declaration, never from its name: panels built by
 * `P()` carry `custom: true` and are wired by hand against `PANEL_ROOT`; panels
 * built by `D()` are rendered by `DeclaredPanel` out of the root's own key.
 */
export function rootBoardSources(root: RootLike,
                                 panelRoot: Record<string, string>): string[] {
  const custom = root.panels.length > 0 && root.panels.every((p) => p.custom === true);
  if (!custom) return [root.key];
  return Array.from(new Set(Object.values(panelRoot)));
}

/**
 * THE ROOT DOT. `live` when a root it reads holds something, `empty` when they
 * are all bare, `unread` when the board could not be read at all.
 */
export function rootDataState(
  root: RootLike,
  board: Record<string, unknown> | null | undefined,
  panelRoot: Record<string, string>,
  ready?: boolean,
): RootDataState {
  // THE READ HAS NOT LANDED. Not empty — empty is a claim that the panels have
  // nothing, and nobody has looked yet.
  if (ready === false) return 'unread';
  if (!board || typeof board !== 'object' || Array.isArray(board)) return 'unread';
  const sources = rootBoardSources(root, panelRoot);
  return sources.some((k) => hasBoardData(
    Object.prototype.hasOwnProperty.call(board, k)
      ? (board as Record<string, unknown>)[k] : undefined,
  )) ? 'live' : 'empty';
}
